/**
 * Shared post-login redirect sanitization.
 *
 * Used by both the signin page (server component) and the signin server
 * action to prevent open-redirect attacks. Keeping a single implementation
 * avoids logic drift between the two code paths.
 */

export const DEFAULT_POST_LOGIN_REDIRECT = "/home";

const DANGEROUS_SCHEME_RE = /^(javascript|data|vbscript):/i;

/** Placeholder origin for resolving a candidate; never sent anywhere. */
const RESOLVE_BASE = "http://app.invalid";

const MAX_REDIRECT_LENGTH = 2048;

/** Encoded layers inspected on top of the decode Next.js already did. */
const MAX_DECODE_DEPTH = 3;

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\u0000-\u001F\u007F]/;

function decodingLevels(value: string): string[] {
  const levels = [value];
  let current = value;
  for (let i = 0; i < MAX_DECODE_DEPTH; i++) {
    if (!current.includes("%")) break;
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      levels.push(next);
      current = next;
    } catch {
      break;
    }
  }
  return levels;
}

/**
 * A candidate is safe when its path part (before `?` or `#`) starts with a
 * single `/`, has no control characters or backslashes, and resolves to
 * the app's own origin. Browsers drop tab and newline and turn `\` into
 * `/`, so `/\t/evil.example` or `/\evil.example` would otherwise become
 * the protocol-relative `//evil.example`.
 */
function isSameOriginPath(candidate: string): boolean {
  const path = candidate.split(/[?#]/, 1)[0] ?? "";
  if (CONTROL_CHAR_RE.test(path) || path.includes("\\")) return false;
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  try {
    return new URL(candidate, RESOLVE_BASE).origin === RESOLVE_BASE;
  } catch {
    return false;
  }
}

/**
 * Sanitize a callback / post-login URL.
 *
 * Only same-origin relative paths (starting with a single `/`) are
 * accepted. Everything else falls back to {@link DEFAULT_POST_LOGIN_REDIRECT}.
 *
 * Rejected patterns:
 * - Non-string or empty values, and values over 2048 characters
 * - Protocol-relative URLs (`//evil.com`)
 * - Backslashes in the path (`/\evil.com`; browsers normalize `\` to `/`)
 * - Control characters (`/%09/evil.com`; browsers drop tab and newline)
 * - Any of the above hidden under up to three layers of percent-encoding
 *   (`/%2F/evil.com`, `%2F%2Fevil.com`, `/%255C/evil.com`)
 * - Dangerous URI schemes (`javascript:`, `data:`, `vbscript:`)
 * - Absolute URLs (`https://...`)
 *
 * The accepted value is returned in the form a browser resolves it to
 * (`pathname + search + hash`), so dot segments cannot turn it into
 * `//host` after the check.
 *
 * NOTE: This intentionally does NOT enforce a path allowlist (e.g.
 * only `/home`, `/samples/...`). If that becomes desirable for phishing
 * mitigation, add a regex/prefix allowlist here and update the tests.
 */
export function sanitizePostLoginRedirect(url: unknown): string {
  if (
    typeof url !== "string" ||
    url.length === 0 ||
    url.length > MAX_REDIRECT_LENGTH
  ) {
    return DEFAULT_POST_LOGIN_REDIRECT;
  }

  if (DANGEROUS_SCHEME_RE.test(url) || CONTROL_CHAR_RE.test(url)) {
    return DEFAULT_POST_LOGIN_REDIRECT;
  }

  if (!decodingLevels(url).every(isSameOriginPath)) {
    return DEFAULT_POST_LOGIN_REDIRECT;
  }

  const resolved = new URL(url, RESOLVE_BASE);
  const normalized = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  if (!isSameOriginPath(normalized)) {
    return DEFAULT_POST_LOGIN_REDIRECT;
  }

  return normalized;
}

/**
 * Build a hard-navigation href to sign-in after a client-side session failure.
 *
 * Returns `null` when already on an auth page so callers can skip and avoid
 * redirect storms. Uses `callbackUrl` (not `next`) so the sign-in page can
 * return the user after login.
 */
export function buildSessionExpiredSignInHref(
  currentPathWithSearch?: string,
): string | null {
  if (globalThis.window !== undefined) {
    const { pathname } = globalThis.window.location;
    if (
      pathname.startsWith("/signin") ||
      pathname.startsWith("/signup") ||
      pathname.startsWith("/forgot-password") ||
      pathname.startsWith("/reset-password")
    ) {
      return null;
    }
  }

  const params = new URLSearchParams({ error: "SessionExpired" });
  if (currentPathWithSearch !== undefined) {
    params.set("callbackUrl", sanitizePostLoginRedirect(currentPathWithSearch));
  }

  return `/signin?${params.toString()}`;
}
